import { ReactNode, Fragment, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import {
  getBlogPostByUid,
  getRecentBlogPosts,
} from '@adapters/prismic/fetching/blog';
import { BlogPost } from '@adapters/prismic/types/blogPost';
import prismicLinkResolver from '@adapters/prismic/utils/linkResolver';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import ContentPageSidebar from '@components/content/ContentPageSidebar';
import ContentPageHeading from '@components/content/ContentPageHeading';
import ContentPageContent from '@components/content/ContentPageContent';
import SliceHandler from '@components/cms/SliceHandler';
import BlogArticlesGrid from '@components/cms/BlogArticlesGrid';
import Heading from '@components/ui/Heading';
import BannerImage from '@components/ui/BannerImage';
import Divider from '@components/ui/Divider';
import ShareButton from '@components/ui/ShareButton';
import type {
  GetStaticPropsContext,
  InferGetStaticPropsType,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { handlePromoDetailsTracking } from '@utils/gtm';
import { StructuredBlogData } from '@components/StructuredData/StructuredBlogData';

import { altLocale } from '@utils/locale';
import { MEC_TITLE } from '@utils/constants';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './Article.module.scss';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

// Author image widths mirror Article.module.scss ($image-width-mobile/desktop).
// Inlined here because Turbopack's CSS pipeline does not support ICSS `:export`.
const MOBILE_AUTHOR_IMAGE_WIDTH = '7.375rem'; // rem(118px)
const DESKTOP_AUTHOR_IMAGE_WIDTH = '15rem'; // rem(240px)

interface BlogPostsProps {
  preview: boolean;
  alternateLocaleUrl?: string;
  blogPost: BlogPost;
  canonicalUrl: string;
  langAltUrl: string;
  recentBlogPosts: BlogPost[];
  promoMessages?: PromoTicker[];
  stores: MyStoreData[];
  xDefault: string;
}

export const getStaticProps = async ({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<BlogPostsProps>
> => {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Blog post has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const [
    blogPost,
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    recentBlogPosts,
    promoBarData,
  ] = await Promise.allSettled([
    getBlogPostByUid(params.slug, pageLocale, previewRef),
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'blogArticle', 'promotions']),
    getRecentBlogPosts(pageLocale, previewRef),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  if (!blogPost) {
    console.warn(`Blog post with slug '${params?.slug}' not found`);
    return {
      notFound: true,
    };
  }

  const canonicalUrl = process.env.BASE_URL + prismicLinkResolver(blogPost);

  let langAltUrl = null;
  if (blogPost.alternate_languages && blogPost.alternate_languages.length > 0) {
    langAltUrl =
      process.env.BASE_URL +
      prismicLinkResolver(blogPost.alternate_languages[0]);
  }

  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      blogPost,
      canonicalUrl,
      langAltUrl,
      preview,
      stores,
      recentBlogPosts,
      ...(blogPost.alternate_languages.length && {
        alternateLocaleUrl: prismicLinkResolver(
          blogPost.alternate_languages[0]
        ),
      }),
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function BlogPostPage({
  blogPost,
  recentBlogPosts,
  canonicalUrl,
  langAltUrl,
  xDefault,
}: InferGetStaticPropsType<typeof getStaticProps>): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation(['common', 'blogArticle']);

  const {
    data: {
      title,
      hero_image: heroImage,
      body,
      categories,
      author,
      formattedDate,
      meta_title: metaTitle,
      meta_description: metaDescription,
    },
  } = blogPost;
  const categoryHasData = categories.filter((el) => el.category.data);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  const categoryList = categoryHasData.flatMap(({ category }, i) => {
    if (category.isBroken) return [];
    const uniqueKey = `${category.uid}-${category.slug}`;

    return (
      <Fragment key={uniqueKey}>
        <Link href={prismicLinkResolver(category)}>{category.data.name}</Link>
        {i < categoryHasData.length - 1 && ', '}
      </Fragment>
    );
  });

  const languageAlternates =
    langAltUrl && xDefault
      ? [
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]
      : [];

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={languageAlternates}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          locale,
          images: [
            {
              url: heroImage.url,
              width: heroImage.dimensions.width,
              height: heroImage.dimensions.height,
              alt: heroImage.alt,
            },
          ],
        }}
      />
      <StructuredBlogData blogData={blogPost} />
      <BannerImage image={heroImage} />
      <Container className={styles.blogContentContainer}>
        {author?.data && (
          <ContentPageSidebar classNames={styles.blogSidebar}>
            <div className={styles.authorInfoContainer}>
              {author.data?.user_image && (
                <div className={styles.authorImage}>
                  <Image
                    src={author.data.user_image.url}
                    alt={author.data.user_image.alt}
                    fill
                    sizes={`(min-width: 720px) ${DESKTOP_AUTHOR_IMAGE_WIDTH}, ${MOBILE_AUTHOR_IMAGE_WIDTH}`}
                  />
                </div>
              )}
              <div>
                {author.data?.name && (
                  <Heading
                    styleLevel="5"
                    semanticLevel="h2"
                    className={styles.authorName}
                  >
                    {author.data.name}
                  </Heading>
                )}
                {author.data?.bio && (
                  <p className={styles.authorBio}>{author.data.bio}</p>
                )}
              </div>
            </div>
          </ContentPageSidebar>
        )}

        <ContentPageHeading>{title}</ContentPageHeading>

        <ContentPageContent classNames={styles.blogContent}>
          <p className={styles.blogPostDate}>{formattedDate}</p>
          <p className={styles.blogPostCategories}>
            {`${t('blogArticle:blog.article.found.in')} `}
            &ldquo;{categoryList}&rdquo;
          </p>

          {body?.map((slice) => (
            <SliceHandler
              key={`article-slice-${slice.id}`}
              slice={slice}
              pageType="blog"
            />
          ))}

          <Divider className={styles.blogShareDivider} />
          <ShareButton
            url={canonicalUrl}
            shareImageUrl={heroImage.url}
            emailSubject={metaTitle}
            emailBody={`${metaTitle}%20(${canonicalUrl})`}
            buttonText={t('common:share.button')}
          />
        </ContentPageContent>
      </Container>
      <Container className={styles.recentBlogsContainer}>
        <BlogArticlesGrid
          blogPosts={recentBlogPosts}
          title={t('common:recent.articles.heading')}
          linkTitle={t('common:recent.articles.button')}
        />
      </Container>
    </>
  );
}

BlogPostPage.Layout = Layout;
